// ==========================================================================
// DCF ENGINE — moteur de valorisation extrait de App.jsx (fonction pure).
//
// Extraction sous protocole capture-d'abord : les sorties du code d'origine
// ont été capturées en VECTEUR COMPLET (FCFF par année, somme actualisée, VT,
// WACC/ROIC/réinvestissement terminaux, multiples implicites) dans
// src/__fixtures__/dcf_golden.json AVANT le déplacement, puis vérifiées
// identiques après (dcfEngine.test.js). Tout écart du golden est une
// régression du moteur, pas un bruit d'affichage — une VT qui dérive de 15 %
// ne se voit pas à l'œil nu.
//
// Corps déplacé VERBATIM — ne pas « nettoyer » sans faire tourner le golden.
// ==========================================================================

export function calcDCF(p){
  const proj=[];let rev=p.base_revenue;
  const useCW=p.use_client_wacc&&p.client_wacc>0;
  const useCP=p.use_client_projections&&p.client_projections&&p.client_projection_years>0;
  const cpN=useCP?Math.min(p.client_projection_years,p.client_projections.length):0;
  for(let y=1;y<=p.forecast_years;y++){
    let gr,mg,ebit,ebitda,nopat,reinv,fcff;
    if(useCP&&y<=cpN){
      const cp=p.client_projections[y-1];const prevRev=y===1?p.base_revenue:proj[y-2].revenue;
      rev=cp.revenue||0;gr=prevRev>0?(rev-prevRev)/prevRev:0;
      ebitda=cp.ebitda||0;ebit=ebitda-(cp.da||0);mg=rev>0?ebit/rev:0;
      nopat=ebit*(1-p.tax_rate);reinv=(cp.capex||0)+(cp.delta_nwc||0)-(cp.da||0);fcff=nopat-reinv;
    }else{
      const t=Math.min(y/p.growth_convergence_year,1);gr=p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*t;
      rev*=(1+gr);const tm=Math.min(y/p.margin_convergence_year,1);
      mg=p.base_operating_margin+(p.target_operating_margin-p.base_operating_margin)*tm;
      ebit=rev*mg;ebitda=ebit;nopat=ebit*(1-p.tax_rate); // convergence : pas de D&A séparée → EBITDA≈EBIT
      reinv=p.sales_to_capital_ratio?((rev-(y===1?p.base_revenue:proj[y-2].revenue))/p.sales_to_capital_ratio):0;fcff=nopat-reinv;
    }
    const wacc=useCW?p.client_wacc:(p.cost_of_capital_initial+(p.cost_of_capital_terminal-p.cost_of_capital_initial)*Math.min(y/p.wacc_convergence_year,1));
    const ic=y===1?(p.sales_to_capital_ratio?p.base_revenue/p.sales_to_capital_ratio:p.base_revenue):proj[y-2]._ic+reinv,roic=nopat/ic;
    proj.push({year:y,revenue:rev,revenue_growth_rate:gr,operating_margin:mg,ebit,ebitda,nopat,reinvestment:reinv,fcff,wacc,roic,_ic:ic});
  }
  let cum=1,sumPV=0;for(const x of proj){cum*=(1+x.wacc);x.discount_factor=1/cum;x.pv_fcff=x.fcff*x.discount_factor;sumPV+=x.pv_fcff;}
  // ── Valeur terminale NORMATIVE ────────────────────────────────────────────
  // On n'extrapole PAS le dernier FCFF explicite : il est dimensionné pour la
  // croissance de la phase (réinvestissement lourd), donc faux en régime
  // permanent. On reconstruit un flux normatif : NOPAT terminal × (1 − taux de
  // réinvestissement), avec taux = g / ROIC (Damodaran). La croissance
  // perpétuelle finance ainsi son propre capital ; ROIC planché au WACC
  // terminal (pas de destruction de valeur en perpétuité).
  const _last=proj[proj.length-1];
  const _g=p.terminal_growth_rate, _waccT=_last.wacc;
  // Plancher ROIC terminal ≥ WACC (anti-destruction de valeur en perpétuité).
  // Ce plancher est un CLAMP : il ne doit pas être silencieux. On expose le ROIC
  // RÉEL (_roicRaw, éventuellement < 0) ET un drapeau quand le clamp s'active —
  // un ROIC ramené de −171 % à WACC est une information, pas un détail interne.
  // Sinon l'invariant « ROIC∞ ≥ WACC∞ » ne fait que constater ce que le clamp
  // vient d'imposer : il ne peut pas échouer, donc il ne teste rien.
  const _roicRaw=_last.roic;
  const _roicFloored=!(isFinite(_roicRaw)&&_roicRaw>0)||_roicRaw<_waccT;
  const _roicT=_roicFloored?_waccT:_roicRaw; // valeur EFFECTIVE (clampée) utilisée par le réinvestissement
  const _reinvT=(_g>0&&_roicT>0)?Math.min(_g/_roicT,1):0;
  const _fcffT=(_last.nopat*(1+_g))*(1-_reinvT);
  const _capRate=_waccT-_g;
  const tv=_capRate>0?_fcffT/_capRate:null;
  const pvTV=tv!=null?tv*_last.discount_factor:0;
  const ev=sumPV+pvTV,eq=ev-p.net_debt-p.minority_interests;
  // GARDE-FOU n.s. — un DCF dont l'INTÉGRALITÉ de l'horizon explicite est en
  // résultat d'exploitation négatif (et donc l'année terminale, qui en dérive)
  // n'est pas une valorisation basse : c'est une valorisation qui n'a pas lieu
  // d'être. La méthode ne convient pas au dossier → on rend n.s. plutôt qu'un EV
  // négatif, exactement comme les multiples sur base ≤ 0 (not_meaningful). Le
  // détail (projections, VT) reste pour montrer POURQUOI, mais l'entête est nulle.
  const _dcfNS=proj.every(x=>x.ebit<0);
  // Multiple de sortie implicite = VT non actualisée / EBITDA terminal — garde-fou
  const _ebitdaT=(_last.ebitda!=null?_last.ebitda:_last.ebit)*(1+_g);
  const implied_exit_multiple_ebitda=(tv!=null&&_ebitdaT>0)?tv/_ebitdaT:null;
  return{company_name:p.company_name,not_meaningful:_dcfNS,ns_reason:_dcfNS?"horizon_explicite_et_terminal_negatifs":null,equity_value:_dcfNS?null:eq,enterprise_value:_dcfNS?null:ev,value_per_share:_dcfNS?null:(p.shares_outstanding>0?eq/p.shares_outstanding:null),terminal_value:pvTV,sum_pv_fcff:sumPV,implied_exit_multiple:implied_exit_multiple_ebitda,implied_exit_multiple_nopat:(tv!=null&&_last.nopat>0)?tv/_last.nopat:null,terminal_wacc:_waccT,terminal_roic:_roicT,terminal_roic_raw:_roicRaw,terminal_roic_floored:_roicFloored,terminal_reinvestment_rate:_reinvT,terminal_invested_capital:_last._ic,base_invested_capital:p.base_invested_capital??null,terminal_value_pct:(ev!==0&&isFinite(ev))?pvTV/ev:null,projections:proj.map(x=>({year:x.year,revenue:x.revenue,revenue_growth_rate:x.revenue_growth_rate,operating_margin:x.operating_margin,nopat:x.nopat,fcff:x.fcff,roic:x.roic,wacc:x.wacc,pv_fcff:x.pv_fcff}))};
}
